import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

// Three-deck broadsheet masthead:
//   1. a slim emerald utility strip carrying the dateline and account links,
//   2. a white masthead deck with the Lora wordmark,
//   3. a bordered department row of rule-separated sections.
export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const dateline = new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 glass border-b border-line" data-testid="navbar">
            {/* Deck 1 — emerald utility strip */}
            <div className="bg-accent text-accent-foreground">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-9 gap-4">
                        <time className="hidden md:block font-mono text-[11px] uppercase tracking-[0.08em] text-accent-foreground/80">
                            {dateline}
                        </time>
                        <div className="flex items-center gap-5 ml-auto">
                            <button
                                onClick={() => setSearchOpen((v) => !v)}
                                className="inline-flex items-center text-accent-foreground/80 hover:text-accent-foreground transition-colors duration-150"
                                aria-label="Search"
                                data-testid="navbar-search-toggle"
                            >
                                <Search size={15} />
                            </button>
                            {user ? (
                                <div className="hidden sm:flex items-center gap-5">
                                    <Link
                                        to="/account"
                                        data-testid="navbar-account"
                                        className="inline-flex items-center gap-1.5 eyebrow text-accent-foreground/85 hover:text-accent-foreground transition-colors duration-150"
                                    >
                                        <User size={12} />
                                        {user.name?.split(" ")[0]}
                                    </Link>
                                    <button
                                        onClick={() => { logout(); navigate("/"); }}
                                        data-testid="navbar-logout"
                                        className="eyebrow text-accent-foreground/85 hover:text-accent-foreground transition-colors duration-150"
                                    >
                                        Log out
                                    </button>
                                </div>
                            ) : (
                                <div className="hidden sm:flex items-center gap-5">
                                    <Link
                                        to="/login"
                                        data-testid="navbar-login"
                                        className="eyebrow text-accent-foreground/85 hover:text-accent-foreground transition-colors duration-150"
                                    >
                                        Log in
                                    </Link>
                                    <Link
                                        to="/register"
                                        data-testid="navbar-register"
                                        className="eyebrow bg-surface text-accent px-4 py-1.5 hover:bg-accent-soft transition-colors duration-150"
                                    >
                                        Subscribe
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Deck 2 — masthead */}
            <div className="bg-surface border-b border-line">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-4 py-3 sm:py-4">
                        <button
                            className="lg:hidden -ml-1 p-2 text-accent"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Toggle menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>

                        <Link to="/" className="flex items-baseline gap-3 min-w-0" data-testid="navbar-logo">
                            <span className="font-display text-2xl sm:text-[2rem] font-bold leading-none text-accent truncate">
                                {brand.site_name}
                            </span>
                            <span className="hidden sm:block h-4 w-px bg-line shrink-0" />
                            <span className="hidden sm:block eyebrow text-dim shrink-0">{brand.domain}</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Deck 3 — department row */}
            <nav className="hidden lg:block bg-surface border-b border-line">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <ul className="flex items-stretch -mx-4">
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="relative group">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className="flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-semibold uppercase tracking-[0.07em] text-foreground border-b-2 border-transparent hover:text-accent hover:border-accent transition-colors duration-150"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && (
                                        <ChevronDown size={12} className="text-dim transition-transform duration-200 group-hover:rotate-180" />
                                    )}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-0 top-full w-60 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 z-50">
                                        <div className="bg-surface border border-line border-t-2 border-t-accent shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="block px-4 py-2.5 text-[13px] text-foreground border-b border-line last:border-0 hover:bg-accent-soft hover:text-accent transition-colors duration-150"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                        <li className="ml-auto flex">
                            <Link
                                to="/archive"
                                data-testid="nav-archive"
                                className="flex items-center px-4 py-2.5 text-[12.5px] font-semibold uppercase tracking-[0.07em] text-dim border-b-2 border-transparent hover:text-accent hover:border-accent transition-colors duration-150"
                            >
                                Archive
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            {/* Search panel */}
            {searchOpen && (
                <div className="border-b border-line bg-surface-sunken" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-2">
                        <div className="relative flex-1">
                            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-dim pointer-events-none" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles, markets, authors..."
                                data-testid="navbar-search-input"
                                className="field pl-10"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="eyebrow bg-accent text-accent-foreground px-7 hover:bg-foreground transition-colors duration-150"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="lg:hidden border-b border-line bg-surface" data-testid="mobile-menu">
                    <ul>
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="border-b border-line">
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="block px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.07em] text-foreground hover:text-accent transition-colors duration-150"
                                >
                                    {CATEGORY_LABELS[cat]}
                                </Link>
                            </li>
                        ))}
                        <li className="border-b border-line">
                            <Link
                                to="/archive"
                                onClick={() => setMobileOpen(false)}
                                className="block px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.07em] text-dim hover:text-accent transition-colors duration-150"
                            >
                                Archive
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-3 p-5">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center eyebrow border border-accent text-accent py-3">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center eyebrow bg-accent text-accent-foreground py-3">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="p-5 space-y-3">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block text-center eyebrow border border-accent text-accent py-3">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full eyebrow border border-line text-neg py-3">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
